import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Save, Plus, Trash2, Check, AlertCircle, Briefcase, Search, Building, Link2, ExternalLink, Edit, ArrowLeft, MapPin } from 'lucide-react';
import { UserHeader } from '../components/UserHeader';
import axios from 'axios';
import { MUNICIPIOS, AREAS_TRABAJO, TIPOS_INSTITUCION, LOCALIDADES_POR_MUNICIPIO, tieneLocalidadesPredefinidas, getLocalidades } from '../utils/constants';
import DireccionAsistida from '../components/DireccionAsistida';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Trabajo vacío - ahora con institucion_id para vincular
const emptyTrabajo = {
  id: '',
  institucion_id: null, // ID de la institución vinculada
  institucion_nombre: '', // Para mostrar el nombre si está vinculada
  // Campos propios del trabajo (no de la institución)
  nombre_area: '', // Área interna donde trabaja
  demandas_respuesta: '',
  comentarios_indicaciones: '',
  // Campos legacy para instituciones no vinculadas
  municipio_trabajo: '',
  areas_trabajo: [],
  tipo_institucion: '',
  nombre_institucion: '',
  domicilio_institucion: '',
  usar_domicilio_laboral: false,
  telefono_institucion: '',
  email_institucion: '',
  horarios_atencion: '',
  responsable_nombre: ''
};

export default function GestionarRecurseroPage() {
  const { trabajador, updateTrabajador } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [nuevoTrabajoId, setNuevoTrabajoId] = useState(null);

  const [recurseroVisible, setRecurseroVisible] = useState(false);
  const [telefonoVisible, setTelefonoVisible] = useState(false);
  const [trabajos, setTrabajos] = useState([]);
  
  const nuevoTrabajoRef = useRef(null);

  // Estados para el autocomplete de instituciones
  const [searchQueries, setSearchQueries] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [showResults, setShowResults] = useState({});
  const [searchLoading, setSearchLoading] = useState({});
  
  // Estado para modal de crear institución
  const [showCrearInstitucion, setShowCrearInstitucion] = useState(null);
  const [mostrarMapaUbicacion, setMostrarMapaUbicacion] = useState(false);
  const [nuevaInstitucion, setNuevaInstitucion] = useState({
    nombre: '',
    tipo_institucion: '',
    areas: [],
    telefono: '',
    email: '',
    web: '',
    nombre_organismo: '',
    area_desempeno: '',
    demandas_atiende: '',
    indicaciones: '',
    horarios: '',
    responsable: '',
    // Datos de dirección asistida
    direccion: {
      municipio: '',
      localidad: '',
      calle: '',
      numero: '',
      lat: null,
      lon: null,
      direccion_texto: '',
      precision: '',
      fuente_geocodificacion: ''
    }
  });
  
  // Estado para modal de vista previa/edición antes de vincular
  const [showPrevistaInstitucion, setShowPrevistaInstitucion] = useState(null); // {index, institucion}
  const [editandoPrevista, setEditandoPrevista] = useState(false);
  const [datosPrevista, setDatosPrevista] = useState(null);
  const [guardandoPrevista, setGuardandoPrevista] = useState(false);
  
  // Estado para detección de duplicados
  const [posiblesDuplicados, setPosiblesDuplicados] = useState([]);
  const [buscandoDuplicados, setBuscandoDuplicados] = useState(false);

  useEffect(() => {
    if (trabajador?.datos_recursero) {
      setRecurseroVisible(trabajador.datos_recursero.recursero_visible || false);
      setTelefonoVisible(trabajador.datos_recursero.telefono_visible || false);
      setTrabajos(trabajador.datos_recursero.trabajos || []);
    }
  }, [trabajador]);

  // Debounce para búsqueda de instituciones
  const searchInstituciones = useCallback(async (query, index) => {
    if (!query || query.length < 2) {
      setSearchResults(prev => ({ ...prev, [index]: [] }));
      return;
    }

    setSearchLoading(prev => ({ ...prev, [index]: true }));
    try {
      const response = await axios.get(`${API_URL}/instituciones/autocomplete`, {
        params: { q: query }
      });
      setSearchResults(prev => ({ ...prev, [index]: response.data }));
    } catch (err) {
      console.error('Error buscando instituciones:', err);
      setSearchResults(prev => ({ ...prev, [index]: [] }));
    } finally {
      setSearchLoading(prev => ({ ...prev, [index]: false }));
    }
  }, []);

  // Debounce effect
  useEffect(() => {
    const timeouts = {};
    Object.entries(searchQueries).forEach(([index, query]) => {
      timeouts[index] = setTimeout(() => {
        searchInstituciones(query, index);
      }, 300);
    });
    return () => {
      Object.values(timeouts).forEach(clearTimeout);
    };
  }, [searchQueries, searchInstituciones]);

  // Detectar duplicados cuando cambian los campos relevantes
  useEffect(() => {
    const detectarDuplicados = async () => {
      if (!nuevaInstitucion.nombre_organismo || !nuevaInstitucion.municipio) {
        setPosiblesDuplicados([]);
        return;
      }
      
      if (nuevaInstitucion.nombre_organismo.length < 3) {
        setPosiblesDuplicados([]);
        return;
      }

      setBuscandoDuplicados(true);
      try {
        const params = new URLSearchParams({
          nombre_organismo: nuevaInstitucion.nombre_organismo,
          municipio: nuevaInstitucion.municipio
        });
        if (nuevaInstitucion.nombre) params.append('nombre', nuevaInstitucion.nombre);
        if (nuevaInstitucion.area_desempeno) params.append('area_desempeno', nuevaInstitucion.area_desempeno);
        
        const response = await axios.post(`${API_URL}/instituciones/detectar-duplicados?${params.toString()}`);
        setPosiblesDuplicados(response.data);
      } catch (err) {
        console.error('Error detectando duplicados:', err);
        setPosiblesDuplicados([]);
      } finally {
        setBuscandoDuplicados(false);
      }
    };

    const timeout = setTimeout(detectarDuplicados, 500);
    return () => clearTimeout(timeout);
  }, [nuevaInstitucion.nombre_organismo, nuevaInstitucion.municipio, nuevaInstitucion.nombre, nuevaInstitucion.area_desempeno]);

  const handleRecurseroVisibleChange = (checked) => {
    setRecurseroVisible(checked);
  };

  const handleTelefonoVisibleChange = (checked) => {
    setTelefonoVisible(checked);
  };

  const addTrabajo = () => {
    const newId = `temp-${Date.now()}`;
    setTrabajos(prev => [{ ...emptyTrabajo, id: newId }, ...prev]);
    setNuevoTrabajoId(newId);
    
    setTimeout(() => {
      nuevoTrabajoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => setNuevoTrabajoId(null), 2000);
    }, 100);
  };

  const removeTrabajo = (index) => {
    setTrabajos(prev => prev.filter((_, i) => i !== index));
    // Limpiar estados de búsqueda
    setSearchQueries(prev => { const n = {...prev}; delete n[index]; return n; });
    setSearchResults(prev => { const n = {...prev}; delete n[index]; return n; });
    setShowResults(prev => { const n = {...prev}; delete n[index]; return n; });
  };

  const updateTrabajo = (index, field, value) => {
    setTrabajos(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  // Abrir vista previa de institución antes de vincular
  const abrirPrevistaInstitucion = (index, institucion) => {
    setShowPrevistaInstitucion({ index, institucion });
    setDatosPrevista({
      nombre_organismo: institucion.nombre_organismo || institucion.nombre || '',
      area_desempeno: institucion.area_desempeno || '',
      telefonos: institucion.telefonos?.join(', ') || '',
      emails: institucion.emails?.join(', ') || '',
      domicilio: institucion.domicilio?.calle || '',
      horarios: institucion.horarios || '',
      demandas_atiende: institucion.demandas_atiende || '',
      indicaciones: institucion.indicaciones || ''
    });
    setEditandoPrevista(false);
    setShowResults(prev => ({ ...prev, [index]: false }));
  };

  // Guardar cambios de la vista previa y vincular
  const guardarYVincular = async () => {
    if (!showPrevistaInstitucion || !datosPrevista) return;
    
    const { index, institucion } = showPrevistaInstitucion;
    
    // Si hay cambios, guardarlos primero
    if (editandoPrevista) {
      setGuardandoPrevista(true);
      try {
        const dataToSend = {
          nombre_organismo: datosPrevista.nombre_organismo,
          area_desempeno: datosPrevista.area_desempeno,
          telefonos: datosPrevista.telefonos.split(',').map(t => t.trim()).filter(Boolean),
          emails: datosPrevista.emails.split(',').map(e => e.trim()).filter(Boolean),
          domicilio: { calle: datosPrevista.domicilio, numero: '', observaciones: '' },
          horarios: datosPrevista.horarios,
          demandas_atiende: datosPrevista.demandas_atiende,
          indicaciones: datosPrevista.indicaciones
        };
        
        await axios.put(`${API_URL}/instituciones/${institucion.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        // Actualizar la institución con los nuevos datos
        institucion.nombre_organismo = datosPrevista.nombre_organismo;
        institucion.area_desempeno = datosPrevista.area_desempeno;
      } catch (err) {
        console.error('Error guardando cambios:', err);
        setError('Error al guardar los cambios');
        setGuardandoPrevista(false);
        return;
      }
      setGuardandoPrevista(false);
    }
    
    // Vincular la institución
    vincularInstitucion(index, institucion);
    
    // Cerrar modal
    setShowPrevistaInstitucion(null);
    setDatosPrevista(null);
    setEditandoPrevista(false);
  };

  // Vincular trabajo a una institución existente (directo, sin edición)
  const vincularInstitucion = (index, institucion) => {
    setTrabajos(prev => prev.map((t, i) => {
      if (i !== index) return t;
      return {
        ...t,
        institucion_id: institucion.id,
        institucion_nombre: institucion.nombre,
        nombre_organismo: institucion.nombre_organismo || institucion.nombre,
        area_desempeno: institucion.area_desempeno || '',
        // Copiar datos de la institución para mostrar en el recursero
        municipio_trabajo: institucion.municipio,
        areas_trabajo: institucion.areas || [],
        tipo_institucion: institucion.tipo_institucion,
        nombre_institucion: institucion.nombre_organismo || institucion.nombre
      };
    }));
    // Limpiar búsqueda
    setSearchQueries(prev => ({ ...prev, [index]: '' }));
    setShowResults(prev => ({ ...prev, [index]: false }));
  };

  // Desvincular institución
  const desvincularInstitucion = (index) => {
    setTrabajos(prev => prev.map((t, i) => {
      if (i !== index) return t;
      return {
        ...t,
        institucion_id: null,
        institucion_nombre: '',
        nombre_organismo: '',
        area_desempeno: '',
        municipio_trabajo: '',
        areas_trabajo: [],
        tipo_institucion: '',
        nombre_institucion: ''
      };
    }));
  };

  // Crear nueva institución
  const handleCrearInstitucion = async (index) => {
    try {
      const dir = nuevaInstitucion.direccion;
      
      // Preparar datos con el nuevo formato
      const datosEnviar = {
        nombre: nuevaInstitucion.nombre,
        tipo_institucion: nuevaInstitucion.tipo_institucion,
        areas: nuevaInstitucion.areas,
        telefono: nuevaInstitucion.telefono,
        email: nuevaInstitucion.email,
        web: nuevaInstitucion.web,
        nombre_organismo: nuevaInstitucion.nombre_organismo,
        area_desempeno: nuevaInstitucion.area_desempeno,
        demandas_atiende: nuevaInstitucion.demandas_atiende,
        indicaciones: nuevaInstitucion.indicaciones,
        horarios: nuevaInstitucion.horarios,
        responsable: nuevaInstitucion.responsable,
        // Datos de ubicación
        municipio: dir.municipio,
        localidad: dir.localidad,
        domicilio: dir.calle && dir.numero ? `${dir.calle} ${dir.numero}` : '',
        coordenadas: dir.lat && dir.lon ? { lat: dir.lat, lng: dir.lon } : null,
        direccion_texto: dir.direccion_texto,
        precision_geocodificacion: dir.precision,
        fuente_geocodificacion: dir.fuente_geocodificacion
      };
      
      const response = await axios.post(`${API_URL}/instituciones/crear-minima`, datosEnviar, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Vincular la nueva institución al trabajo
      vincularInstitucion(index, response.data);
      
      // Limpiar y cerrar modal
      setShowCrearInstitucion(null);
      setMostrarMapaUbicacion(false);
      setNuevaInstitucion({
        nombre: '',
        tipo_institucion: '',
        areas: [],
        telefono: '',
        email: '',
        web: '',
        nombre_organismo: '',
        area_desempeno: '',
        demandas_atiende: '',
        indicaciones: '',
        horarios: '',
        responsable: '',
        direccion: {
          municipio: '',
          localidad: '',
          calle: '',
          numero: '',
          lat: null,
          lon: null,
          direccion_texto: '',
          precision: '',
          fuente_geocodificacion: ''
        }
      });
      setPosiblesDuplicados([]);
    } catch (err) {
      console.error('Error creando institución:', err);
      setError('Error al crear la institución');
    }
  };

  const handleUsarDomicilioLaboral = (index, checked) => {
    setTrabajos(prev => prev.map((t, i) => {
      if (i !== index) return t;
      
      if (checked && trabajador?.domicilio_laboral) {
        const dom = trabajador.domicilio_laboral;
        const direccion = [
          dom.calle,
          dom.numero,
          dom.piso ? `Piso ${dom.piso}` : '',
          dom.depto ? `Depto ${dom.depto}` : '',
          dom.localidad
        ].filter(Boolean).join(' ');
        
        return { 
          ...t, 
          usar_domicilio_laboral: true,
          domicilio_institucion: direccion
        };
      } else {
        return { 
          ...t, 
          usar_domicilio_laboral: false,
          domicilio_institucion: ''
        };
      }
    }));
  };

  const toggleAreaTrabajo = (index, area) => {
    setTrabajos(prev => prev.map((t, i) => {
      if (i !== index) return t;
      const areas = t.areas_trabajo.includes(area)
        ? t.areas_trabajo.filter(a => a !== area)
        : [...t.areas_trabajo, area];
      return { ...t, areas_trabajo: areas };
    }));
  };

  const toggleNuevaInstitucionArea = (area) => {
    setNuevaInstitucion(prev => ({
      ...prev,
      areas: prev.areas.includes(area)
        ? prev.areas.filter(a => a !== area)
        : [...prev.areas, area]
    }));
  };

  // Validación de trabajo completo
  const trabajoEstaVacio = (trabajo) => {
    // Un trabajo está vacío si no tiene institución vinculada
    return !trabajo.institucion_id;
  };

  const validarTrabajoCompleto = (trabajo, index) => {
    const errores = [];
    
    // Si está vinculado a institución, no se requieren campos adicionales
    // Toda la información está en la ficha de la institución
    if (trabajo.institucion_id) {
      // No hay campos obligatorios adicionales cuando está vinculado
      return { valido: true };
    }
    
    // Si no está vinculado, el trabajo no es válido - debe vincular una institución
    errores.push('Debes vincular una institución');
    
    if (errores.length > 0) {
      return { valido: false, trabajo: index + 1, campos: errores };
    }
    return { valido: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const trabajosNoVacios = trabajos.filter(t => !trabajoEstaVacio(t));
      
      // Validar trabajos
      for (let i = 0; i < trabajosNoVacios.length; i++) {
        const resultado = validarTrabajoCompleto(trabajosNoVacios[i], i);
        if (!resultado.valido) {
          setError(`Trabajo #${resultado.trabajo}: Faltan campos obligatorios: ${resultado.campos.join(', ')}`);
          setLoading(false);
          return;
        }
      }

      const trabajosLimpios = trabajosNoVacios.map(t => ({
        id: t.id || `temp-${Date.now()}`,
        institucion_id: t.institucion_id || null,
        institucion_nombre: t.institucion_nombre || '',
        nombre_organismo: t.nombre_organismo || '',
        area_desempeno: t.area_desempeno || '',
        municipio_trabajo: t.municipio_trabajo || '',
        areas_trabajo: t.areas_trabajo || [],
        tipo_institucion: t.tipo_institucion || '',
        nombre_institucion: t.nombre_institucion || '',
        nombre_area: t.nombre_area || '',
        demandas_respuesta: t.demandas_respuesta || '',
        comentarios_indicaciones: t.comentarios_indicaciones || '',
        domicilio_institucion: t.domicilio_institucion || '',
        usar_domicilio_laboral: t.usar_domicilio_laboral || false,
        telefono_institucion: t.telefono_institucion || '',
        email_institucion: t.email_institucion || '',
        horarios_atencion: t.horarios_atencion || '',
        responsable_nombre: t.responsable_nombre || ''
      }));

      const datosRecursero = {
        recursero_visible: recurseroVisible,
        telefono_visible: telefonoVisible,
        trabajos: trabajosLimpios
      };

      await updateTrabajador({ datos_recursero: datosRecursero });
      setShowSuccessModal(true);
      
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 2000);
    } catch (err) {
      console.error('Error guardando recursero:', err);
      setError('Error al guardar los datos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <UserHeader />
      
      <div className="max-w-4xl mx-auto py-8 px-4 animate-fade-in">
        {/* Actions */}
        <div className="flex items-center justify-end mb-6">
          <Button onClick={handleSubmit} disabled={loading} className="gap-2">
            <Save className="w-4 h-4" />
            {loading ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Gestionar mi Recursero
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Visibilidad */}
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Aparecer en el Recursero</Label>
                  <p className="text-sm text-gray-500">
                    Tu información será visible en la guía pública de recursos
                  </p>
                </div>
                <Switch
                  checked={recurseroVisible}
                  onCheckedChange={handleRecurseroVisibleChange}
                />
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t border-blue-200">
                <div>
                  <Label className="text-base font-medium">Mostrar teléfono personal</Label>
                  <p className="text-sm text-gray-500">
                    Tu celular será visible en tu ficha del recursero
                  </p>
                </div>
                <Switch
                  checked={telefonoVisible}
                  onCheckedChange={handleTelefonoVisibleChange}
                />
              </div>
            </div>

            {/* Trabajos */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Mis Trabajos</h3>
                <Button onClick={addTrabajo} variant="outline" size="sm" className="gap-1">
                  <Plus className="w-4 h-4" />
                  Agregar Trabajo
                </Button>
              </div>

              {trabajos.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500 mb-3">No tenés trabajos cargados</p>
                  <Button onClick={addTrabajo} variant="outline" size="sm" className="gap-1">
                    <Plus className="w-4 h-4" />
                    Agregar mi primer trabajo
                  </Button>
                </div>
              ) : (
                trabajos.map((trabajo, index) => (
                  <div 
                    key={trabajo.id || index} 
                    ref={trabajo.id === nuevoTrabajoId ? nuevoTrabajoRef : null}
                    className={`border rounded-lg p-6 space-y-4 transition-all duration-500 ${
                      trabajo.id === nuevoTrabajoId 
                        ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200' 
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900">
                        {trabajo.id === nuevoTrabajoId && <span className="text-emerald-600">✨ Nuevo - </span>}
                        Trabajo #{index + 1}
                      </h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTrabajo(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* SECCIÓN: Vincular Institución */}
                    <div className="p-4 bg-white rounded-lg border border-blue-200">
                      <Label className="text-base font-medium flex items-center gap-2 mb-3">
                        <Building className="w-5 h-5 text-blue-600" />
                        Institución
                      </Label>

                      {trabajo.institucion_id ? (
                        // Institución vinculada
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Link2 className="w-5 h-5 text-blue-600" />
                            <div>
                              <p className="font-medium text-gray-900">
                                {trabajo.nombre_organismo || trabajo.institucion_nombre}
                              </p>
                              {trabajo.area_desempeno && (
                                <p className="text-sm text-blue-600">{trabajo.area_desempeno}</p>
                              )}
                              <p className="text-sm text-gray-500">{trabajo.municipio_trabajo}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/instituciones/${trabajo.institucion_id}`)}
                              className="text-blue-600"
                              title="Ver ficha"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/instituciones/${trabajo.institucion_id}?editar=true`)}
                              className="text-green-600 border-green-300 hover:bg-green-50"
                              title="Editar institución"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => desvincularInstitucion(index)}
                              className="text-red-500"
                            >
                              Desvincular
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Buscar o crear institución
                        <div className="space-y-3">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                              placeholder="Buscar por organismo o área..."
                              value={searchQueries[index] || ''}
                              onChange={(e) => {
                                setSearchQueries(prev => ({ ...prev, [index]: e.target.value }));
                                setShowResults(prev => ({ ...prev, [index]: true }));
                              }}
                              onFocus={() => setShowResults(prev => ({ ...prev, [index]: true }))}
                              className="pl-10"
                            />
                            
                            {/* Resultados de búsqueda */}
                            {showResults[index] && searchQueries[index]?.length >= 2 && (
                              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                                {searchLoading[index] ? (
                                  <div className="p-3 text-center text-gray-500">Buscando...</div>
                                ) : searchResults[index]?.length > 0 ? (
                                  <>
                                    {searchResults[index].map(inst => (
                                      <div
                                        key={inst.id}
                                        className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-0 transition-colors"
                                        onClick={() => abrirPrevistaInstitucion(index, inst)}
                                      >
                                        <p className="font-medium text-gray-900">
                                          {inst.nombre_organismo || inst.nombre}
                                          {inst.area_desempeno && (
                                            <span className="text-blue-600"> — {inst.area_desempeno}</span>
                                          )}
                                        </p>
                                        <p className="text-sm text-gray-500">
                                          {inst.localidad ? `${inst.localidad}, ` : ''}{inst.municipio} • {inst.tipo_institucion}
                                        </p>
                                      </div>
                                    ))}
                                    {/* Opción para crear nueva área en organismo existente */}
                                    <div 
                                      className="p-3 bg-amber-50 hover:bg-amber-100 cursor-pointer border-t-2 border-amber-200 transition-colors"
                                      onClick={() => {
                                        // Pre-llenar el organismo si hay resultados
                                        const primerResultado = searchResults[index]?.[0];
                                        if (primerResultado?.nombre_organismo) {
                                          setNuevaInstitucion(prev => ({
                                            ...prev,
                                            nombre_organismo: primerResultado.nombre_organismo,
                                            municipio: primerResultado.municipio || '',
                                            tipo_institucion: primerResultado.tipo_institucion || ''
                                          }));
                                        }
                                        setShowCrearInstitucion(index);
                                        setShowResults(prev => ({ ...prev, [index]: false }));
                                      }}
                                    >
                                      <p className="font-medium text-amber-800">
                                        + ¿Tu área no aparece? Crear nueva área
                                      </p>
                                      <p className="text-sm text-amber-600">
                                        Agregá tu área dentro del mismo organismo
                                      </p>
                                    </div>
                                  </>
                                ) : (
                                  <div className="p-3 text-center text-gray-500">
                                    No se encontraron instituciones
                                  </div>
                                )}
                                
                                {/* Opción crear nueva */}
                                <div
                                  className="p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer border-t flex items-center gap-2 text-blue-600"
                                  onClick={() => {
                                    setShowCrearInstitucion(index);
                                    setNuevaInstitucion(prev => ({ ...prev, nombre: searchQueries[index] || '' }));
                                    setShowResults(prev => ({ ...prev, [index]: false }));
                                  }}
                                >
                                  <Plus className="w-4 h-4" />
                                  <span>Crear nueva institución</span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <p className="text-xs text-gray-500">
                            Buscá una institución existente o creá una nueva si no existe
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Campos del trabajo */}
                    {trabajo.institucion_id ? (
                      // Trabajo vinculado a institución - sin campos adicionales
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700">
                          ✅ Los datos de contacto, demandas e indicaciones se toman de la ficha institucional y se mantienen actualizados de forma colaborativa.
                        </p>
                      </div>
                    ) : (
                      // Trabajo sin vincular - mensaje para crear institución
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-amber-800 font-medium mb-2">⚠️ Debes vincular una institución</p>
                        <p className="text-sm text-amber-700 mb-3">
                          Buscá una institución existente en el campo de arriba, o creá una nueva si no existe.
                        </p>
                        <Button
                          variant="outline"
                          className="border-amber-400 text-amber-700 hover:bg-amber-100"
                          onClick={() => setShowCrearInstitucion(index)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Crear nueva institución
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal Crear Institución */}
      {showCrearInstitucion !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Crear Nueva Institución
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del Organismo <span className="text-red-500">*</span></Label>
                <Input
                  value={nuevaInstitucion.nombre_organismo}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, nombre_organismo: e.target.value }))}
                  placeholder="Ej: Ministerio de Desarrollo, HIGA Pte. Perón, Escuela N°5"
                />
                <p className="text-xs text-gray-500">El organismo o institución principal</p>
              </div>

              <div className="space-y-2">
                <Label>Área de Desempeño</Label>
                <Input
                  value={nuevaInstitucion.area_desempeno}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, area_desempeno: e.target.value }))}
                  placeholder="Ej: Dirección de Asistencia Crítica, Servicio de Traumatología, Equipo de Orientación Escolar"
                />
                <p className="text-xs text-gray-500">El área específica dentro del organismo (opcional)</p>
              </div>

              <div className="space-y-2">
                <Label>Tipo de Institución <span className="text-red-500">*</span></Label>
                <Select 
                  value={nuevaInstitucion.tipo_institucion} 
                  onValueChange={(v) => setNuevaInstitucion(prev => ({ ...prev, tipo_institucion: v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Seleccioná" /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_INSTITUCION.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Sugerencias de duplicados */}
              {(posiblesDuplicados.length > 0 || buscandoDuplicados) && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm font-medium text-yellow-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {buscandoDuplicados ? 'Buscando instituciones similares...' : '¿Ya existe esta institución?'}
                  </p>
                  {posiblesDuplicados.length > 0 && (
                    <div className="space-y-2">
                      {posiblesDuplicados.map(dup => (
                        <div 
                          key={dup.id}
                          className="p-2 bg-white rounded border border-yellow-200 cursor-pointer hover:bg-yellow-50 transition-colors"
                          onClick={() => {
                            vincularInstitucion(showCrearInstitucion, dup);
                            setShowCrearInstitucion(null);
                            setNuevaInstitucion({
                              nombre: '', tipo_institucion: '', municipio: '', localidad: '',
                              areas: [], telefono: '', email: '', web: '', nombre_organismo: '', area_desempeno: '',
                              demandas_atiende: '', indicaciones: '', horarios: '', domicilio: '', responsable: ''
                            });
                            setPosiblesDuplicados([]);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{dup.nombre}</p>
                              {dup.nombre_organismo && <p className="text-xs text-gray-500">{dup.nombre_organismo}</p>}
                              <p className="text-xs text-gray-500">{dup.localidad ? `${dup.localidad}, ` : ''}{dup.municipio}</p>
                            </div>
                            <Badge variant="outline" className="bg-yellow-100 text-yellow-700">
                              {dup.similitud}% similar
                            </Badge>
                          </div>
                        </div>
                      ))}
                      <p className="text-xs text-yellow-700 mt-2">
                        Hacé click en una institución para vincularla, o continuá completando el formulario si es nueva.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Áreas de intervención</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 p-3 bg-gray-50 rounded-lg border">
                  {AREAS_TRABAJO.map(area => (
                    <div key={area} className="flex items-center space-x-2">
                      <Checkbox
                        id={`nueva-area-${area}`}
                        checked={nuevaInstitucion.areas.includes(area)}
                        onCheckedChange={() => toggleNuevaInstitucionArea(area)}
                      />
                      <label htmlFor={`nueva-area-${area}`} className="text-sm cursor-pointer leading-tight">
                        {area}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dirección Asistida con Geocodificación */}
              <div className="space-y-2 border rounded-lg p-4 bg-gray-50">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Ubicación de la Institución
                </Label>
                <DireccionAsistida
                  value={nuevaInstitucion.direccion}
                  onChange={(dir) => setNuevaInstitucion(prev => ({ ...prev, direccion: dir }))}
                  showMap={true}
                  mapHeight="250px"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono {!nuevaInstitucion.email && <span className="text-red-500">*</span>}</Label>
                  <Input
                    value={nuevaInstitucion.telefono}
                    onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, telefono: e.target.value }))}
                    placeholder="011-xxxx-xxxx"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email {!nuevaInstitucion.telefono && <span className="text-red-500">*</span>}</Label>
                  <Input
                    type="email"
                    value={nuevaInstitucion.email}
                    onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contacto@institucion.gob.ar"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">Al menos uno (teléfono o email) es obligatorio</p>

              <div className="space-y-2">
                <Label>Sitio web</Label>
                <Input
                  value={nuevaInstitucion.web}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, web: e.target.value }))}
                  placeholder="www.institucion.gob.ar"
                />
              </div>

              <div className="space-y-2">
                <Label>Horarios de atención</Label>
                <Input
                  value={nuevaInstitucion.horarios}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, horarios: e.target.value }))}
                  placeholder="Ej: Lunes a Viernes 8-14hs"
                />
              </div>

              <div className="space-y-2">
                <Label>Demandas que atiende <span className="text-red-500">*</span></Label>
                <Textarea
                  value={nuevaInstitucion.demandas_atiende}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, demandas_atiende: e.target.value }))}
                  placeholder="Describí las demandas que atiende el área/institución"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Indicaciones / Criterios institucionales</Label>
                <Textarea
                  value={nuevaInstitucion.indicaciones}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, indicaciones: e.target.value }))}
                  placeholder="Información útil para colegas (requisitos, documentación, tips para derivar)"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Responsable / Referente del área</Label>
                <Input
                  value={nuevaInstitucion.responsable}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, responsable: e.target.value }))}
                  placeholder="Ej: Lic. María García"
                />
                <p className="text-xs text-gray-500">Nombre del responsable o referente institucional (opcional)</p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCrearInstitucion(null)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={() => handleCrearInstitucion(showCrearInstitucion)}
                  className="flex-1"
                  disabled={
                    !nuevaInstitucion.nombre_organismo ||
                    !nuevaInstitucion.tipo_institucion || 
                    !nuevaInstitucion.direccion?.municipio ||
                    (tieneLocalidadesPredefinidas(nuevaInstitucion.direccion?.municipio) && !nuevaInstitucion.direccion?.localidad) ||
                    (nuevaInstitucion.direccion?.localidad === 'Otro' && !nuevaInstitucion.localidad_otra) ||
                    (!nuevaInstitucion.telefono && !nuevaInstitucion.email) ||
                    !nuevaInstitucion.demandas_atiende
                  }
                >
                  Crear y vincular
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Vista Previa / Edición antes de vincular */}
      {showPrevistaInstitucion && datosPrevista && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className={editandoPrevista ? "bg-green-50" : "bg-blue-50"}>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  {editandoPrevista ? 'Editar Institución' : 'Revisar antes de vincular'}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setShowPrevistaInstitucion(null);
                    setDatosPrevista(null);
                    setEditandoPrevista(false);
                  }}
                >
                  ✕
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {!editandoPrevista ? (
                // Vista previa (solo lectura)
                <>
                  <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Organismo</p>
                      <p className="font-medium">{datosPrevista.nombre_organismo}</p>
                    </div>
                    {datosPrevista.area_desempeno && (
                      <div>
                        <p className="text-sm text-gray-500">Área</p>
                        <p className="font-medium text-blue-600">{datosPrevista.area_desempeno}</p>
                      </div>
                    )}
                    {datosPrevista.domicilio && (
                      <div>
                        <p className="text-sm text-gray-500">Domicilio</p>
                        <p className="font-medium">{datosPrevista.domicilio}</p>
                      </div>
                    )}
                    {datosPrevista.telefonos && (
                      <div>
                        <p className="text-sm text-gray-500">Teléfonos</p>
                        <p className="font-medium">{datosPrevista.telefonos}</p>
                      </div>
                    )}
                    {datosPrevista.emails && (
                      <div>
                        <p className="text-sm text-gray-500">Emails</p>
                        <p className="font-medium">{datosPrevista.emails}</p>
                      </div>
                    )}
                    {datosPrevista.horarios && (
                      <div>
                        <p className="text-sm text-gray-500">Horarios</p>
                        <p className="font-medium">{datosPrevista.horarios}</p>
                      </div>
                    )}
                    {datosPrevista.demandas_atiende && (
                      <div>
                        <p className="text-sm text-gray-500">Demandas que atiende</p>
                        <p className="font-medium">{datosPrevista.demandas_atiende}</p>
                      </div>
                    )}
                    {datosPrevista.indicaciones && (
                      <div>
                        <p className="text-sm text-gray-500">Indicaciones</p>
                        <p className="font-medium">{datosPrevista.indicaciones}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setEditandoPrevista(true)}
                      className="flex-1"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar datos
                    </Button>
                    <Button 
                      onClick={guardarYVincular}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Vincular
                    </Button>
                  </div>
                </>
              ) : (
                // Modo edición
                <>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Organismo</Label>
                      <Input
                        value={datosPrevista.nombre_organismo}
                        onChange={(e) => setDatosPrevista(prev => ({ ...prev, nombre_organismo: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Área</Label>
                      <Input
                        value={datosPrevista.area_desempeno}
                        onChange={(e) => setDatosPrevista(prev => ({ ...prev, area_desempeno: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Domicilio</Label>
                      <Input
                        value={datosPrevista.domicilio}
                        onChange={(e) => setDatosPrevista(prev => ({ ...prev, domicilio: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Teléfonos</Label>
                        <Input
                          value={datosPrevista.telefonos}
                          onChange={(e) => setDatosPrevista(prev => ({ ...prev, telefonos: e.target.value }))}
                          placeholder="Separados por coma"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Emails</Label>
                        <Input
                          value={datosPrevista.emails}
                          onChange={(e) => setDatosPrevista(prev => ({ ...prev, emails: e.target.value }))}
                          placeholder="Separados por coma"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Horarios</Label>
                      <Input
                        value={datosPrevista.horarios}
                        onChange={(e) => setDatosPrevista(prev => ({ ...prev, horarios: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Demandas que atiende</Label>
                      <Textarea
                        value={datosPrevista.demandas_atiende}
                        onChange={(e) => setDatosPrevista(prev => ({ ...prev, demandas_atiende: e.target.value }))}
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Indicaciones</Label>
                      <Textarea
                        value={datosPrevista.indicaciones}
                        onChange={(e) => setDatosPrevista(prev => ({ ...prev, indicaciones: e.target.value }))}
                        rows={2}
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setEditandoPrevista(false)}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={guardarYVincular}
                      disabled={guardandoPrevista}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {guardandoPrevista ? 'Guardando...' : 'Guardar y vincular'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Success */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-sm mx-4 animate-fade-in">
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">¡Guardado exitosamente!</h3>
              <p className="text-gray-500">Tus datos del recursero han sido actualizados</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Botón volver flotante en la parte inferior */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40">
        <Button 
          variant="outline" 
          onClick={() => navigate('/recursero')}
          className="shadow-lg bg-white"
          data-testid="back-to-recursero-menu-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Recursero
        </Button>
      </div>
    </div>
  );
}
